/** Progressive enhancement only; CSS performs all frame-by-frame motion. */
const initializeLogoStrip = (root: HTMLElement) => {
    if (root.dataset.initialized || root.classList.contains('is-editor')) return;
    const viewport = root.querySelector<HTMLElement>('.stgb-ai-logo-strip__viewport');
    const track = root.querySelector<HTMLElement>('.stgb-ai-logo-strip__track');
    const sequence = track?.querySelector<HTMLElement>('.stgb-ai-logo-strip__sequence');
    if (!viewport || !track || !sequence || !sequence.children.length) return;
    root.dataset.initialized = 'true';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let copies: HTMLElement[] = [];
    let frame = 0;
    let distance = 0;

    const clearCopies = () => { copies.forEach(copy => copy.remove()); copies = []; };
    const measure = () => {
        frame = 0;
        if (reducedMotion.matches) {
            delete root.dataset.enhanced;
            delete root.dataset.ready;
            clearCopies();
            return;
        }
        root.dataset.enhanced = 'true';
        const width = sequence.getBoundingClientRect().width;
        const available = viewport.getBoundingClientRect().width;
        if (!width || !available) return;
        // One original plus ceil(viewport / sequence) copies covers both ends
        // of a complete translation. One pixel guards subpixel rounding.
        const count = Math.max(1, Math.ceil((available + 1) / width));
        if (copies.length !== count) {
            clearCopies();
            const fragment = document.createDocumentFragment();
            for (let i = 0; i < count; i++) {
                const copy = sequence.cloneNode(true) as HTMLElement;
                copy.setAttribute('aria-hidden', 'true');
                copy.setAttribute('inert', '');
                copy.removeAttribute('aria-label');
                copy.dataset.stgbLogoCopy = 'true';
                fragment.appendChild(copy);
                copies.push(copy);
            }
            track.appendChild(fragment);
        }
        if (width !== distance) {
            root.style.setProperty('--stgb-logo-distance', -width + 'px');
            distance = width;
        }
        root.dataset.ready = 'true';
    };
    const schedule = () => { if (!frame) frame = window.requestAnimationFrame(measure); };
    // Keep the icon's space and the visible name even if a Media Library image is missing.
    root.addEventListener('error', (event) => {
        if (event.target instanceof HTMLImageElement) event.target.style.visibility = 'hidden';
    }, true);
    root.querySelectorAll<HTMLImageElement>('img').forEach(img => {
        if (img.complete && !img.naturalWidth) img.style.visibility = 'hidden';
    });
    reducedMotion.addEventListener('change', schedule);
    if (typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(schedule);
        observer.observe(viewport);
        observer.observe(sequence);
    } else {
        window.addEventListener('resize', schedule, { passive: true });
    }
    document.fonts?.ready.then(schedule);
    schedule();
};

const initialize = () => document.querySelectorAll<HTMLElement>('.stgb-ai-logo-strip').forEach(initializeLogoStrip);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
else initialize();

export {};
